import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Row, Col, Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function AddOffer() {
  const baseURL = process.env.REACT_APP_API_URL;
  const { id } = useParams(); // edit mode (optional)
  const navigate = useNavigate();
  const token = localStorage.getItem("profileToken");

  const initialState = {
    text: "",
    offer_tag: "",
    phone_no: "",
    brand_id: "",
    status: 1,
  };


  const [formData, setFormData] = useState(initialState);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ Fetch brands for dropdown (same token style)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${baseURL}/brand/list`, {
          headers: { "x-access-token": token },
        });
        if (data.status && Array.isArray(data.result)) {
          setBrands(data.result);
        } else {
          setBrands([]);
        }
      } catch (err) {
        console.error("❌ Error fetching brands:", err);
      }
    })();
  }, [baseURL, token]);

  // ✅ Fetch offer details when editing
  useEffect(() => {
    if (!id) return;

    (async () => {
      try {
        const { data } = await axios.get(`${baseURL}/offer/${id}`, {
          headers: { "x-access-token": token },
        });

        if (data.status && data.result) {
          const o = data.result;
          setFormData({
            text: o.text || "",
            offer_tag: o.offer_tag || "",
            phone_no: o.phone_no || "",
            brand_id: o.brand_id ? String(o.brand_id) : "",
            status: o.status !== undefined ? Number(o.status) : 1,
          });
        } else {
          Swal.fire("Error", "Offer not found", "error");
        }
      } catch (err) {
        console.error("❌ Error fetching offer:", err);
        Swal.fire("Error", "Failed to fetch offer details", "error");
      }
    })();
  }, [id, baseURL, token]);

  // ✅ Handle inputs (same like AddBrand pattern)
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // ✅ Reset
  const handleReset = () => {
    setFormData(initialState);
  };

  // ✅ Submit handler with Swal popup + redirect to offer list
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        text: formData.text,
        offer_tag: formData.offer_tag || null,
        phone_no: formData.phone_no || null,
        brand_id: formData.brand_id ? Number(formData.brand_id) : null,
        status: formData.status !== undefined ? Number(formData.status) : 1,
      };


      const headers = { "x-access-token": token };

      if (id) {
        await axios.put(`${baseURL}/offer/update/${id}`, payload, { headers });
        await Swal.fire("Success", "✅ Offer updated successfully!", "success");
      } else {
        await axios.post(`${baseURL}/offer/add`, payload, { headers });
        await Swal.fire("Success", "✅ Offer added successfully!", "success");
      }

      // ✅ Redirect to offer list
      navigate("/offer-list");
    } catch (err) {
      console.error("❌ Error saving offer:", err);
      Swal.fire("Error", "❌ Failed to save offer", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container my-4">
      <h3 className="fw-bold text-center mb-4 text-success">
        {id ? "Update Offer" : "Add New Offer"}
      </h3>

      <form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col xs={12} md={12}>
            <div className="border border-success rounded-4 p-4">
              <Row>
                <Col xs={12} md={6}>
                  <div className="mb-1">
                    <label htmlFor="offer_tag" className="form-label">
                      Offer Tag
                    </label>
                    <input
                      type="text"
                      id="offer_tag"
                      className="form-control"
                      placeholder="e.g. Spacial Offer"
                      value={formData.offer_tag}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label htmlFor="phone_no" className="form-label">
                      Phone No
                    </label>
                    <input
                      type="text"
                      id="phone_no"
                      className="form-control"
                      placeholder="e.g. 9876543210"
                      value={formData.phone_no}
                      onChange={handleChange}
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label htmlFor="text" className="form-label">
                      Offer Text <span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="text"
                      className="form-control"
                      placeholder="Enter offer details"
                      rows={4}
                      value={formData.text}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label htmlFor="brand_id" className="form-label">
                      Brand <span className="text-danger">*</span>
                    </label>
                    <select
                      id="brand_id"
                      className="form-select"   // ✅ form-control -> form-select
                      value={formData.brand_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Brand</option>
                      {brands.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>

                  </div>
                </Col>
              </Row>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  variant="outline-dark"
                  className="rounded-pill px-4"
                  onClick={handleReset}
                  type="button"
                >
                  Reset
                </Button>

                <Button
                  variant="success"
                  className="rounded-pill px-4"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : id ? "Update" : "Save"}
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </form>
    </div>
  );
}

export default AddOffer;
